import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Button, FormControl, InputLabel, Select, MenuItem, Tab, Tabs, Typography, Switch, FormControlLabel } from "@mui/material";
import { useEffect, useState, useMemo, Fragment } from "react";
import { ProjectActivity } from "../../types/ActivityTypes";
import { getWorkFrontDetailsByProject, getConsolidatedYield, ConsolidatedYieldActivity } from "../../api/consolidatedPerformanceApi";
import { getProjectActivities } from "../../api/activityApi";
import { profiles } from "../../contexts/constants/profiles";
import { saveAs } from "file-saver";
import ExcelJS from "exceljs";
import logo from "../../assets/images/logoElecnor.png";
import { Chart } from "react-chartjs-2";
import { Chart as ChartJS, BarController, LineController, BarElement, LinearScale, CategoryScale, LineElement, PointElement, Tooltip, Legend, ChartData, ChartOptions } from "chart.js";
import { getStatistics, getProjectById } from "../../api/projectApi";
import { ProjectStatistics } from "../../types/projectStatisticsType";
import { Project } from "../../types/projectTypes";
ChartJS.register(BarController, LineController, BarElement, LinearScale, CategoryScale, LineElement, PointElement, Tooltip, Legend);

interface ConsolidatedReportProps {
    selectedProject?: number;
    startDate: string;
    endDate: string;
    filterTrigger: boolean;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

const ConsolidatedPerformanceReport: React.FC<ConsolidatedReportProps> = ({ selectedProject, startDate, endDate, filterTrigger }) => {

    const [groupedData, setGroupedData] = useState<{ [date: string]: { [front: number]: any[] } }>({});
    const [activitiesProject, setActivitiesProject] = useState<ProjectActivity[]>([]);
    const [firstTableData, setFirstTableData] = useState<any[]>([]);
    const [yieldActivities, setYieldActivities] = useState<ConsolidatedYieldActivity[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedTabProduction, setSelectedTabProduction] = useState(0);
    const [selectedTabPerformance, setSelectedTabPerformance] = useState(0);
    const [selectedUnit, setSelectedUnit] = useState<string>("");
    const [selectedUnitPerformance, setSelectedUnitPerformance] = useState<string>("");
    const [selectedUnitPerformanceHM, setSelectedUnitPerformanceHM] = useState<string>("");
    const [userRole, setUserRole] = useState<string>("");

    const [showUnproductive, setShowUnproductive] = useState(false);
    const [selectedTableUnit, setSelectedTableUnit] = useState<string>("Todas");
    const [statisticsData, setStatisticsData] = useState<ProjectStatistics | null>(null);
    const [projectData, setProjectData] = useState<Project | null>(null);

    const formatComma = (val: any): string => {
        if (val === undefined || val === null || val === "-" || val === "NaN" || Number.isNaN(Number(val))) {
            return "-";
        }
        return String(val).replace(/\./g, ",");
    };

    const normalizeUnit = (text: string | undefined) => {
        if (!text) return "";
        return text.replace(/m3/g, "m³");
    };

    const hasSecondaryUnit = (activityObj: any) => {
        const uom = (activityObj.unitOfMeasurement || "").toString().trim().toUpperCase();
        const uomProd = (activityObj.unitOfMeasurementProduction || "TORRE").toString().trim().toUpperCase();
        return uom !== "NA" && uom !== "N/A" && uom !== "" && uom !== uomProd;
    };

    // ¿La unidad de producción de la actividad es "torres"? Si no lo es (ej. km), la columna
    // principal de la tabla de registros debe mostrar el avance real (km), no la suma de % / 100.
    const isProductionInTorres = (activityObj: any) => {
        const uomProd = (activityObj.unitOfMeasurementProduction || "TORRE").toString().trim().toUpperCase();
        return uomProd.includes("TORRE");
    };

    // Valor "Real" calculado: 1 decimal; 0 / NaN / vacío se muestra como "-" (sin dato).
    const fmtReal = (value: number | null | undefined): string => {
        if (value === null || value === undefined) return "-";
        const num = Number(value);
        if (Number.isNaN(num) || num === 0) return "-";
        return num.toFixed(1);
    };

    // Meta P0: se muestra el valor tal cual (incluido 0); solo null/undefined se muestra como "-".
    const fmtP0 = (value: number | null | undefined): string => {
        if (value === null || value === undefined) return "-";
        const num = Number(value);
        if (Number.isNaN(num)) return "-";
        return String(num);
    };

    // Valor de Oferta: se carga a mano en la matriz de actividades y puede no existir.
    // Se muestra igual que P0, pero la ausencia (null) es el caso normal, no una anomalía.
    const fmtOffer = (value: number | null | undefined): string => fmtP0(value);

    // Columna "Pendiente": el dato lo entrega el DP en Planificación y todavía no llega al
    // sistema. La columna se dibuja para respetar el formato del informe, pero siempre vacía.
    const PENDING_PLACEHOLDER = "-";

    // Cada grupo (Producción, Rendimiento HH, Rendimiento HM) se abre en estas 4 subcolumnas.
    const SUB_HEADERS = ["Oferta", "P0", "Real", "Pendiente", "Oferta", "P0", "Real", "Pendiente", "Oferta", "P0", "Real", "Pendiente"];
    const SUB_HEADER_COLORS: Record<string, string> = {
        Oferta: "#DDEBF7",
        P0: "#FFEE99",
        Real: "#f0f0f0",
        Pendiente: "#EDEDED",
    };
    // Celda de "Pendiente": sin dato disponible todavía.
    const pendingCellSx = { border: "1px solid #ccc", fontSize: "11px", textAlign: "center" as const, backgroundColor: "#f7f7f7", color: "#bbb", borderRight: "1px solid black" };
    // Celda de "Oferta": valor contractual cargado a mano en la matriz de actividades.
    const offerCellSx = { border: "1px solid #ccc", fontSize: "11px", textAlign: "center" as const, backgroundColor: "#DDEBF7" };

    // Construye las filas de la tabla resumen a partir del consolidado calculado en el backend.
    // Cada actividad puede traer dos filas (principal + secundaria '↳') ya resueltas por el backend.
    // El toggle showUnproductive alterna la visión Efectiva (false) y Total/Con improductividades (true).
    const buildYieldDisplayRows = () => {
        const effective = !showUnproductive;
        return yieldActivities
            .map((activity, index) => {
                const unitRaw = (activity.unit || "").toString().trim();
                return {
                    key: `yield-${activity.idActivity}-${activity.secondary ? "sec" : "pri"}-${index}`,
                    name: activity.name,
                    subActivity: activity.subActivity,
                    unitRaw,
                    prodOffer: fmtOffer(activity.productionGoalOffer),
                    prodReal: fmtReal(effective ? activity.effectiveProductionPerDay : activity.totalProductionPerDay),
                    prodP0: fmtP0(activity.productionGoalPo),
                    prodPending: PENDING_PLACEHOLDER,
                    hhOffer: fmtOffer(activity.yieldGoalHhOffer),
                    hhReal: fmtReal(effective ? activity.effectiveYieldHh : activity.totalYieldHh),
                    hhP0: fmtP0(activity.yieldGoalHhPo),
                    hhPending: PENDING_PLACEHOLDER,
                    hmOffer: fmtOffer(activity.yieldGoalHmOffer),
                    hmReal: fmtReal(effective ? activity.effectiveYieldHm : activity.totalYieldHm),
                    hmP0: fmtP0(activity.yieldGoalHmPo),
                    hmPending: PENDING_PLACEHOLDER,
                    isSecondary: activity.secondary
                };
            })
            .filter((row) => {
                const normalizedUnit = normalizeUnit(row.unitRaw);
                const upper = normalizedUnit.toUpperCase();
                if (upper === "NA" || upper === "N/A" || normalizedUnit === "") return false;
                return selectedTableUnit === "Todas" || normalizedUnit === selectedTableUnit;
            });
    };

    const handleChangeProduction = (_: React.SyntheticEvent, newValue: number) => setSelectedTabProduction(newValue);
    const handleChangePerformance = (_: React.SyntheticEvent, newValue: number) => setSelectedTabPerformance(newValue);

    useEffect(() => {
        const userStr = localStorage.getItem("user");
        if (userStr) {
            const user = JSON.parse(userStr);
            setUserRole(user.rols?.description);
        }
    }, []);

    useEffect(() => {
        if (selectedProject) {
            fetchData();
            fetchStatisticsProject(selectedProject);
        }
    }, [selectedProject, filterTrigger, startDate, endDate]);

    const fetchData = async () => {
        try {
            setLoading(true);

            if (selectedProject) {
                const [activitiesResponse, workFrontResponse, yieldResponse] = await Promise.all([
                    getProjectActivities(selectedProject),
                    getWorkFrontDetailsByProject(selectedProject, startDate, endDate),
                    getConsolidatedYield(selectedProject, startDate, endDate)
                ]);

                setFirstTableData(workFrontResponse);
                setYieldActivities(Array.isArray(yieldResponse?.activities) ? yieldResponse.activities : []);

                const validActivities = activitiesResponse.filter(item => {
                    const uom = (item.activity.unitOfMeasurementProduction || "").toString().trim().toUpperCase();
                    return uom !== "NA" && uom !== "N/A";
                });

                setActivitiesProject(validActivities);

                if (Array.isArray(workFrontResponse)) {
                    setGroupedData(groupDataByDateAndFront(workFrontResponse));
                } else {
                    console.error("Error: workFrontResponse no es un array", workFrontResponse);
                    setGroupedData({});
                }
            }
        } catch (error) {
            console.error("Error fetching second table data", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchStatisticsProject = async (projectId: number) => {
        try {
            const [statisticsProject, project] = await Promise.all([
                getStatistics(projectId),
                getProjectById(projectId)
            ]);
            setStatisticsData(statisticsProject);
            setProjectData(project);
        } catch (error) {
            console.error("Error al obtener los datos externos", error);
        }
    };

    // =========================================================================
    // MOTOR UNIFICADO DE MATCHING (A PRUEBA DE TILDES Y ESPACIOS)
    // =========================================================================

    const parseVal = (val: any) => {
        if (val === null || val === undefined) return 0;
        if (typeof val === 'string') return Number(val.replace(',', '.'));
        return Number(val);
    };

    const isMatchingActivity = (reportItem: any, projectActivity: ProjectActivity) => {
        if (reportItem.activityId && projectActivity.activity.id) {
            return String(reportItem.activityId) === String(projectActivity.activity.id);
        }
        const normalize = (t: any) => (t || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
        const nameMatch = normalize(reportItem.activityName) === normalize(projectActivity.activity.name);
        const subMatch = normalize(reportItem.subActivity) === normalize(projectActivity.activity.subActivity);
        return nameMatch && subMatch;
    };

    const normText = (t: string | undefined | null) => (t || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();

    const matchesAct = (item: any, keyword: string) => {
        const name = item?.activity?.name || item?.activityName || "";
        return normText(name).includes(keyword);
    };

    // =========================================================================

    const exportToExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Resumen de Rendimientos');
        const startRow = 6;

        const imageResponse = await fetch(logo);
        const imageBuffer = await imageResponse.arrayBuffer();
        const imageId = workbook.addImage({
            buffer: imageBuffer,
            extension: 'png',
        });

        worksheet.addImage(imageId, {
            tl: { col: 0, row: 0 },
            ext: { width: 160, height: 60 },
        });

        const projectName = activitiesProject.length > 0 ? activitiesProject[0]?.project?.name : "";
        if (projectName) {
            const projectCell = worksheet.getCell('D2');
            projectCell.value = `PROYECTO: ${projectName.toUpperCase() || ''}`;
            projectCell.font = { bold: true, size: 18 };
            projectCell.alignment = { horizontal: 'left', vertical: 'middle' };
        }

        const currentDate = new Date();
        const formattedDate = currentDate.toLocaleDateString('es-ES', {
            day: '2-digit', month: '2-digit', year: 'numeric',
        });

        const borderStyle: any = {
            top: { style: 'thin' }, left: { style: 'thin' },
            bottom: { style: 'thin' }, right: { style: 'thin' },
            diagonal: {}
        };

        const headerStyle: Partial<ExcelJS.Style> = {
            font: { bold: true, color: { argb: 'FFFFFFFF' } },
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: '4472C4' } },
            alignment: { vertical: 'middle', horizontal: 'center' },
            border: borderStyle
        };

        worksheet.mergeCells(`A${startRow}:A${startRow + 1}`);
        worksheet.getCell(`A${startRow}`).value = "Actividad";
        worksheet.getCell(`A${startRow}`).style = headerStyle as any;

        worksheet.mergeCells(`B${startRow}:B${startRow + 1}`);
        worksheet.getCell(`B${startRow}`).value = "Unidad";
        worksheet.getCell(`B${startRow}`).style = headerStyle as any;

        // Cada grupo ocupa 4 columnas: Oferta | P0 | Real | Pendiente.
        worksheet.mergeCells(`C${startRow}:F${startRow}`);
        worksheet.getCell(`C${startRow}`).value = "Producción (/día)";
        worksheet.getCell(`C${startRow}`).style = headerStyle as any;

        worksheet.mergeCells(`G${startRow}:J${startRow}`);
        worksheet.getCell(`G${startRow}`).value = "Rendimiento HH";
        worksheet.getCell(`G${startRow}`).style = headerStyle as any;

        worksheet.mergeCells(`K${startRow}:N${startRow}`);
        worksheet.getCell(`K${startRow}`).value = "Rendimiento HM";
        worksheet.getCell(`K${startRow}`).style = headerStyle as any;

        const subHeaderFills: Record<string, string> = {
            Oferta: "DDEBF7", P0: "FFEE99", Real: "F0F0F0", Pendiente: "EDEDED",
        };
        SUB_HEADERS.forEach((label, index) => {
            const cell = worksheet.getCell(startRow + 1, 3 + index);
            cell.value = label;
            cell.alignment = { vertical: 'middle', horizontal: 'center' } as any;
            cell.border = borderStyle;
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: subHeaderFills[label] } } as any;
        });

        let currentRow = startRow + 2;

        buildYieldDisplayRows().forEach((row) => {
            const label = `${row.isSecondary ? "↳ " : ""}${row.name} ${row.subActivity ? `(${row.subActivity})` : ""}`;
            const addedRow = worksheet.addRow([
                label,
                normalizeUnit(row.unitRaw),
                formatComma(row.prodOffer), formatComma(row.prodP0), formatComma(row.prodReal), row.prodPending,
                formatComma(row.hhOffer), formatComma(row.hhP0), formatComma(row.hhReal), row.hhPending,
                formatComma(row.hmOffer), formatComma(row.hmP0), formatComma(row.hmReal), row.hmPending
            ]);
            addedRow.eachCell((c, colNumber) => {
                c.border = borderStyle;
                if (row.isSecondary && colNumber === 1) c.alignment = { indent: 2 } as any;
                if (row.isSecondary && colNumber === 2) c.font = { color: { argb: '808080' } };
            });
            currentRow++;
        });

        const totals = getTotalsGrouped();
        const subtotalsStyle: Partial<ExcelJS.Style> = { font: { bold: true }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D9E1F2' } }, border: borderStyle };

        worksheet.addRow([]);

        // Igual que en pantalla: un total en 0 significa "sin dato", no cero.
        const fmtTotal = (v: number) => (v > 0 ? formatComma(v.toFixed(1)) : "-");
        const phaseTotalRow = (label: string, unit: string, t: { hhOffer: number; hhP0: number; hhReal: number; hmOffer: number; hmP0: number; hmReal: number }) => [
            label, unit,
            "-", "-", "-", PENDING_PLACEHOLDER,
            fmtTotal(t.hhOffer), fmtTotal(t.hhP0), fmtTotal(t.hhReal), PENDING_PLACEHOLDER,
            fmtTotal(t.hmOffer), fmtTotal(t.hmP0), fmtTotal(t.hmReal), PENDING_PLACEHOLDER
        ];

        const totalRows = [
            phaseTotalRow("Obras Civiles (Niv + Hormigón)", "Torre", totals.obras),
            phaseTotalRow("Estructuras (Pre + Montaje)", "Torre", totals.pmTorre),
            phaseTotalRow("Estructuras (Pre + Montaje)", "Tn", totals.pmTn),
            phaseTotalRow("Tendido Conductor", "Km", totals.tendidoConductor),
            phaseTotalRow("Tendido OPGW", "Km", totals.tendidoOpgw)
        ];

        totalRows.forEach(row => {
            const addedRow = worksheet.addRow(row);
            addedRow.eachCell(c => { c.style = subtotalsStyle as any; });
        });

        worksheet.columns.forEach(column => {
            column.width = 20;
        });
        worksheet.getColumn(1).width = 40;

        workbook.xlsx.writeBuffer().then((buffer) => {
            const blob = new Blob([buffer], { type: "application/octet-stream" });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `Resumen_Consolidado_${formattedDate}.xlsx`;
            link.click();
        });
    };

    const groupDataByDateAndFront = (data: any[]) => {
        return data.reduce((acc: any, item) => {
            const rawDate = item.activityDate ? item.activityDate.toString().split('T')[0] : "Sin Fecha";
            const [year, month, day] = rawDate.split('-');
            const dateKey = `${day}-${month}-${year}`;

            let frontKey = "Sin Frente";
            if (item.frontNumber !== null && item.frontNumber !== undefined) {
                frontKey = String(item.frontNumber);
            }

            if (!acc[dateKey]) acc[dateKey] = {};
            if (!acc[dateKey][frontKey]) acc[dateKey][frontKey] = [];

            acc[dateKey][frontKey].push(item);
            return acc;
        }, {});
    };

    const memoizedGroupedData = useMemo(() => groupedData, [groupedData]);

    const exportToExcel2 = async (activitiesProject: any[], groupedData: { [date: string]: { [front: number]: any[] } }) => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Reporte Consolidado");

        const imageResponse = await fetch(logo);
        const imageBuffer = await imageResponse.arrayBuffer();
        const imageId = workbook.addImage({
            buffer: imageBuffer,
            extension: "png",
        });

        worksheet.addImage(imageId, {
            tl: { col: 0, row: 1 },
            ext: { width: 160, height: 60 },
        });

        const projectName = activitiesProject.length > 0 ? activitiesProject[0].project?.name : "Proyecto Desconocido";
        if (projectName) {
            const projectCell = worksheet.getCell("D4");
            projectCell.value = `PROYECTO: ${projectName.toUpperCase() || ''}`;
            projectCell.font = { bold: true, size: 18 };
            projectCell.alignment = { horizontal: "left", vertical: "middle" };
        }

        const currentDate = new Date();
        const formattedDate = currentDate.toLocaleDateString("es-ES", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });

        const dateCell = worksheet.getCell("D5");
        dateCell.value = `FECHA: ${formattedDate}`;
        dateCell.font = { size: 12 };
        dateCell.alignment = { horizontal: "left", vertical: "middle" };

        const startRow = 7;

        const activityHeaders = ["", ""];
        activitiesProject.forEach(activity => {
            if (hasSecondaryUnit(activity.activity)) {
                activityHeaders.push("", activity.activity.name, activity.activity.subActivity || "", "");
            } else {
                activityHeaders.push("", activity.activity.name, activity.activity.subActivity || "");
            }
        });

        worksheet.getRow(startRow).values = activityHeaders;

        const activityHeaderRow = worksheet.getRow(startRow);
        activityHeaderRow.eachCell((cell, colNumber) => {
            if (colNumber > 2) {
                cell.style = {
                    font: { bold: true, color: { argb: "FFFFFFFF" } },
                    fill: { type: "pattern", pattern: "solid", fgColor: { argb: "4472C4" } },
                    alignment: { horizontal: "center", vertical: "middle" },
                    border: { top: { style: "thin" }, bottom: { style: "thin" } }
                };
            }
        });

        const subHeaders = ["Fecha", "Frente"];
        let activityColumnStartIndices: number[] = [];
        let colIndex = 3;

        activitiesProject.forEach(activity => {
            activityColumnStartIndices.push(colIndex);

            subHeaders.push(`Producción (${activity.activity.unitOfMeasurementProduction || 'Torre'})`);
            colIndex++;

            if (hasSecondaryUnit(activity.activity)) {
                subHeaders.push(`Producción (${activity.activity.unitOfMeasurement})`);
                colIndex++;
            }

            subHeaders.push("HH", "HM");
            colIndex += 2;
        });

        worksheet.getRow(startRow + 1).values = subHeaders;

        const subHeaderRow = worksheet.getRow(startRow + 1);
        subHeaderRow.eachCell(cell => {
            cell.style = {
                font: { bold: true },
                alignment: { horizontal: "center", vertical: "middle" },
                border: { top: { style: "thin" }, bottom: { style: "thin" } }
            };
        });

        let currentRow = startRow + 2;
        Object.entries(groupedData).forEach(([date, fronts]) => {
            Object.entries(fronts).forEach(([front, activities]) => {
                const row: any[] = [date, front];

                activitiesProject.forEach(column => {
                    const matchingActivities = activities.filter(a => isMatchingActivity(a, column));

                    const totalProd = matchingActivities.reduce((sum, item) => sum + parseVal(item.totalProduction), 0);
                    const totalAdv = matchingActivities.reduce((sum, item) => sum + parseVal(item.totalAdvance), 0);

                    // Producción en km (u otra unidad física) muestra el avance real, no el % agregado.
                    const primaryValue = isProductionInTorres(column.activity) ? totalProd : totalAdv;
                    row.push(primaryValue !== 0 ? primaryValue : "-");

                    if (hasSecondaryUnit(column.activity)) {
                        row.push(totalAdv !== 0 ? totalAdv : "-");
                    }

                    const totalHh = matchingActivities.reduce((sum, item) => sum + parseVal(item.totalHh), 0);
                    const totalHm = matchingActivities.reduce((sum, item) => sum + parseVal(item.totalHm), 0);
                    row.push(totalHh !== 0 ? totalHh : "-", totalHm !== 0 ? totalHm : "-");
                });

                const addedRow = worksheet.addRow(row);

                addedRow.eachCell((cell, colNumber) => {
                    if (colNumber > 2 && typeof cell.value === 'number') {
                        cell.numFmt = '#,##0.00';
                    }
                });

                currentRow++;
            });

            if (Object.keys(groupedData).length > 1) {
                for (let col = 1; col <= worksheet.columnCount; col++) {
                    const borderCell = worksheet.getCell(currentRow - 1, col);
                    borderCell.border = {
                        ...borderCell.border,
                        bottom: { style: "medium", color: { argb: "000000" } }
                    };
                }
            }
        });

        worksheet.columns.forEach((column, index) => {
            if (index >= 2) {
                let maxLength = 0;
                column.eachCell?.({ includeEmpty: true }, (cell) => {
                    const cellText = cell.value ? cell.value.toString() : "";
                    maxLength = Math.max(maxLength, cellText.length);
                });
                column.width = maxLength + 3;
            } else {
                column.width = 18;
            }
        });

        activityColumnStartIndices.forEach(startIndex => {
            let activityColumnCount = 3;

            const activity = activitiesProject.find((_, idx) => activityColumnStartIndices[idx] === startIndex);
            if (activity && hasSecondaryUnit(activity.activity)) {
                activityColumnCount = 4;
            }

            for (let row = startRow; row <= worksheet.rowCount; row++) {
                const leftBorderCell = worksheet.getCell(row, startIndex);
                leftBorderCell.border = {
                    ...leftBorderCell.border,
                    left: { style: "thin", color: { argb: "000000" } }
                };
            }

            for (let row = startRow; row <= worksheet.rowCount; row++) {
                const rightBorderCell = worksheet.getCell(row, startIndex + activityColumnCount - 1);
                rightBorderCell.border = {
                    ...rightBorderCell.border,
                    right: { style: "thin", color: { argb: "000000" } }
                };
            }
        });

        workbook.xlsx.writeBuffer().then(buffer => {
            const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            saveAs(blob, "Reporte_Consolidado.xlsx");
        }).catch(error => console.error("Error al exportar a Excel:", error));
    };

    const handleUnitChangePerformance = (event: any) => setSelectedUnitPerformance(event.target.value);
    const handleUnitChangePerformanceHM = (event: any) => setSelectedUnitPerformanceHM(event.target.value);

    const availableTableUnits = useMemo(() => {
        const units = new Set<string>();
        activitiesProject.forEach(col => {
            const uProd = (col.activity.unitOfMeasurementProduction || "Torres").toString().trim();
            const normalizedProd = normalizeUnit(uProd);
            if (normalizedProd.toUpperCase() !== "NA" && normalizedProd.toUpperCase() !== "N/A") {
                units.add(normalizedProd);
            }
            if (hasSecondaryUnit(col.activity)) {
                units.add(normalizeUnit(col.activity.unitOfMeasurement));
            }
        });
        return Array.from(units).sort();
    }, [activitiesProject]);

    const availableUnits = useMemo(() => {
        const units = new Set<string>();
        activitiesProject.forEach(col => {
            const primaryUnit = normalizeUnit((col.activity.unitOfMeasurementProduction || "Torres").toString().trim());
            if (primaryUnit.toUpperCase() !== "NA" && primaryUnit.toUpperCase() !== "N/A" && primaryUnit.toLowerCase() !== "torres") {
                units.add(primaryUnit);
            }
            if (hasSecondaryUnit(col.activity)) {
                units.add(normalizeUnit(col.activity.unitOfMeasurement));
            }
        });
        return Array.from(units).sort();
    }, [activitiesProject]);

    const availableUnitsPerformance = useMemo(() => availableUnits, [availableUnits]);
    const availableUnitsPerformanceHM = useMemo(() => availableUnits, [availableUnits]);

    useEffect(() => {
        if (availableUnits.length > 0 && !selectedUnit) setSelectedUnit(availableUnits[0]);
    }, [availableUnits]);

    useEffect(() => {
        if (availableUnitsPerformance.length > 0 && !selectedUnitPerformance) setSelectedUnitPerformance(availableUnitsPerformance[0]);
    }, [availableUnitsPerformance]);

    useEffect(() => {
        if (availableUnitsPerformanceHM.length > 0 && !selectedUnitPerformanceHM) setSelectedUnitPerformanceHM(availableUnitsPerformanceHM[0]);
    }, [availableUnitsPerformanceHM]);


    const chartData: ChartData<"bar" | "line"> = useMemo(() => {
        const activityLabels: string[] = [];
        const realValues: number[] = [];
        const poValues: number[] = [];

        activitiesProject.forEach((column) => {
            const subActivity = column.activity.subActivity ? ` (${column.activity.subActivity})` : "";
            const activityName = `${column.activity.name}${subActivity}`;
            activityLabels.push(activityName || "");

            const activityData = Object.values(memoizedGroupedData)
                .flatMap(fronts => Object.values(fronts).flat())
                .filter(a => isMatchingActivity(a, column));

            const totalProduction = activityData.reduce((sum, act) => sum + parseVal(act.totalProduction), 0);
            const totalHh = activityData.reduce((sum, act) => sum + parseVal(act.totalHh), 0);

            const realValue = totalProduction !== 0 ? totalHh / totalProduction : 0;
            realValues.push(realValue);

            const poValue = column.performacePo ?? 0;
            poValues.push(poValue);
        });

        return {
            labels: activityLabels,
            datasets: [
                { type: "bar", label: "HH/Torre (Real)", data: realValues, backgroundColor: "#0057B7", borderColor: "#004F86", borderWidth: 1, barThickness: 40, order: 1 },
                { type: "line", label: "HH/Torre (PO)", data: poValues, borderColor: "#00FF00", backgroundColor: "#00FF00", borderWidth: 2, pointRadius: 3, fill: false, order: 0 }
            ]
        };
    }, [memoizedGroupedData, activitiesProject]);

    const chartDataPerformanceByUnit: ChartData<"bar" | "line"> = useMemo(() => {
        if (!selectedUnitPerformance) return { labels: [], datasets: [] };

        const activityLabels: string[] = [];
        const realValues: number[] = [];
        const poValues: number[] = [];

        activitiesProject.forEach(column => {
            const primaryUnit = normalizeUnit((column.activity.unitOfMeasurementProduction || "Torres").toString().trim());
            const secondaryUnit = hasSecondaryUnit(column.activity) ? normalizeUnit(column.activity.unitOfMeasurement) : "";

            const isPrimaryMatch = primaryUnit === selectedUnitPerformance;
            const isSecondaryMatch = secondaryUnit === selectedUnitPerformance;

            if (!isPrimaryMatch && !isSecondaryMatch) return;

            const subActivity = column.activity.subActivity ? ` (${column.activity.subActivity})` : "";
            const activityName = `${column.activity.name}${subActivity}`;
            activityLabels.push(activityName || "");

            const activityData = Object.values(memoizedGroupedData)
                .flatMap(fronts => Object.values(fronts).flat())
                .filter(a => isMatchingActivity(a, column));

            let totalAdvance = 0;
            let totalHh = 0;
            let poValue = 0;

            if (isPrimaryMatch) {
                totalAdvance = activityData.reduce((sum, act) => sum + parseVal(act.totalAdvance), 0);
                totalHh = activityData.reduce((sum, act) => sum + parseVal(act.totalHh), 0);
                poValue = column.performacePo ?? 0;
            } else if (isSecondaryMatch) {
                const secData = activityData.filter(a => normalizeUnit(a.unitOfMeasurement) === selectedUnitPerformance);
                totalAdvance = secData.reduce((sum, act) => sum + parseVal(act.totalAdvance), 0);
                totalHh = secData.reduce((sum, act) => sum + parseVal(act.totalHh), 0);
                poValue = column.performanceUnitPo ?? 0;
            }

            const realValue = totalAdvance !== 0 ? totalHh / totalAdvance : 0;
            realValues.push(realValue);
            poValues.push(poValue);
        });

        return {
            labels: activityLabels,
            datasets: [
                { type: "bar", label: `Rendimiento (HH/${selectedUnitPerformance}) - Real`, data: realValues, backgroundColor: "#0057B7", borderColor: "#004F86", borderWidth: 1, barThickness: 40 },
                { type: "line", label: `Rendimiento (HH/${selectedUnitPerformance}) - PO`, data: poValues, borderColor: "#00FF00", backgroundColor: "#00FF00", borderWidth: 2, pointRadius: 3, fill: false }
            ]
        };
    }, [memoizedGroupedData, activitiesProject, selectedUnitPerformance]);

    const chartOptionsUnitHh: ChartOptions<"bar" | "line"> = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: "top", }, }, scales: { x: { title: { display: true, text: "Actividades", }, }, y: { title: { display: true, text: `HH/${normalizeUnit(selectedUnitPerformance)}`, }, beginAtZero: true, }, }, };

    const chartDataHM: ChartData<"bar" | "line"> = useMemo(() => {
        const activityLabels: string[] = [];
        const realValues: number[] = [];
        const poValues: number[] = [];

        activitiesProject.forEach(column => {
            const subActivity = column.activity.subActivity ? ` (${column.activity.subActivity})` : "";
            const activityName = `${column.activity.name}${subActivity}`;
            activityLabels.push(activityName || "");

            const activityData = Object.values(memoizedGroupedData)
                .flatMap(fronts => Object.values(fronts).flat())
                .filter(a => isMatchingActivity(a, column));

            const totalProduction = activityData.reduce((sum, act) => sum + parseVal(act.totalProduction), 0);
            const totalHm = activityData.reduce((sum, act) => sum + parseVal(act.totalHm), 0);

            const realValue = totalProduction !== 0 ? totalHm / totalProduction : 0;
            realValues.push(realValue);

            const poValue = column.performaceHmPo ?? 0;
            poValues.push(poValue);
        });

        return {
            labels: activityLabels,
            datasets: [
                { type: "bar", label: "Rendimiento (HM/Torre) - Real", data: realValues, backgroundColor: "#0057B7", borderColor: "#004F86", borderWidth: 1, barThickness: 40, order: 1 },
                { type: "line", label: "Rendimiento (HM/Torre) - PO", data: poValues, borderColor: "#00FF00", backgroundColor: "#00FF00", borderWidth: 2, pointRadius: 3, fill: false, order: 0 }
            ]
        };
    }, [memoizedGroupedData, activitiesProject]);

    const chartOptionsHm: ChartOptions<"bar" | "line"> = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: "top", }, }, scales: { x: { title: { display: true, text: "Actividades", }, }, y: { title: { display: true, text: "HM/Torre", }, beginAtZero: true, }, }, };

    const chartDataProduction: ChartData<"bar" | "line"> = useMemo(() => {
        const activityLabels: string[] = [];
        const realValues: number[] = [];
        const poValues: number[] = [];

        activitiesProject.forEach(column => {
            const subActivity = column.activity.subActivity ? ` (${column.activity.subActivity})` : "";
            const activityName = `${column.activity.name}${subActivity}`;
            activityLabels.push(activityName || "");

            const activityData = Object.values(memoizedGroupedData)
                .flatMap(fronts => Object.values(fronts).flat())
                .filter(a => isMatchingActivity(a, column));

            const totalProduction = activityData.reduce((sum, act) => sum + parseVal(act.totalProduction), 0);
            const activityCount = activityData.filter(act => showUnproductive ? true : parseVal(act.totalProduction) > 0).length;

            const realValue = activityCount !== 0 ? totalProduction / activityCount : 0;
            realValues.push(realValue);

            const poValue = column.productionGoal ?? 0;
            poValues.push(poValue);
        });

        return {
            labels: activityLabels,
            datasets: [
                { type: "bar", label: `Producción (Torres/día) - Real`, data: realValues, backgroundColor: "#FF5733", borderColor: "#D84315", borderWidth: 1, barThickness: 40, order: 1 },
                { type: "line", label: `Producción (Torres/día) - PO`, data: poValues, borderColor: "#00FF00", backgroundColor: "#00FF00", borderWidth: 2, pointRadius: 3, fill: false, order: 0 }
            ]
        };
    }, [memoizedGroupedData, activitiesProject, showUnproductive]);

    const chartOptionsProduction: ChartOptions<"bar" | "line"> = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: "top", }, }, scales: { x: { title: { display: true, text: "Actividades", }, }, y: { title: { display: true, text: "Torres/día", }, beginAtZero: true, }, }, };

    const chartOptions: ChartOptions<"bar" | "line"> = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: "top", }, }, scales: { x: { title: { display: true, text: "Actividades", }, }, y: { title: { display: true, text: `Producción (${normalizeUnit(selectedUnit)}/día)`, }, beginAtZero: true, }, }, };

    const chartOptionsHhTorre: ChartOptions<"bar" | "line"> = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: "top", }, }, scales: { x: { title: { display: true, text: "Actividades", }, }, y: { title: { display: true, text: "HH/Torre", }, beginAtZero: true, }, }, };

    const chartDataProductionByUnit: ChartData<"bar" | "line"> = useMemo(() => {
        if (!selectedUnit) return { labels: [], datasets: [] };

        const activityLabels: string[] = [];
        const realValues: number[] = [];
        const poValues: number[] = [];

        activitiesProject.forEach(column => {
            const primaryUnit = normalizeUnit((column.activity.unitOfMeasurementProduction || "Torres").toString().trim());
            const secondaryUnit = hasSecondaryUnit(column.activity) ? normalizeUnit(column.activity.unitOfMeasurement) : "";

            const isPrimaryMatch = primaryUnit === selectedUnit;
            const isSecondaryMatch = secondaryUnit === selectedUnit;

            if (!isPrimaryMatch && !isSecondaryMatch) return;

            const subActivity = column.activity.subActivity ? ` (${column.activity.subActivity})` : "";
            const activityName = `${column.activity.name}${subActivity}`;
            activityLabels.push(activityName || "");

            const activityData = Object.values(memoizedGroupedData)
                .flatMap(fronts => Object.values(fronts).flat())
                .filter(a => isMatchingActivity(a, column));

            let totalVal = 0;
            let activityCount = 0;
            let poValue = 0;

            if (isPrimaryMatch) {
                totalVal = activityData.reduce((sum, act) => sum + parseVal(act.totalAdvance), 0);
                activityCount = activityData.filter(act => showUnproductive ? true : parseVal(act.totalAdvance) > 0).length;
                poValue = column.productionGoal ?? 0;
            } else if (isSecondaryMatch) {
                const secData = activityData.filter(a => normalizeUnit(a.unitOfMeasurement) === selectedUnit);
                totalVal = secData.reduce((sum, act) => sum + parseVal(act.totalAdvance), 0);
                activityCount = secData.filter(act => showUnproductive ? true : parseVal(act.totalAdvance) > 0).length;
                poValue = column.performaceUnitGoal ?? 0;
            }

            const realValue = activityCount !== 0 ? totalVal / activityCount : 0;
            realValues.push(realValue);
            poValues.push(poValue);
        });

        return {
            labels: activityLabels,
            datasets: [
                { type: "bar", label: `Producción (${selectedUnit}/día) - Real`, data: realValues, backgroundColor: "#FF5733", borderColor: "#D84315", borderWidth: 1, barThickness: 40, order: 1 },
                { type: "line", label: `Producción (${selectedUnit}/día) - PO`, data: poValues, borderColor: "#00FF00", backgroundColor: "#00FF00", borderWidth: 2, pointRadius: 3, fill: false, order: 0 }
            ]
        };
    }, [memoizedGroupedData, activitiesProject, selectedUnit, showUnproductive]);

    const chartDataPerformanceByUnitHM: ChartData<"bar" | "line"> = useMemo(() => {
        if (!selectedUnitPerformanceHM) return { labels: [], datasets: [] };

        const activityLabels: string[] = [];
        const realValues: number[] = [];
        const poValues: number[] = [];

        activitiesProject.forEach(column => {
            const primaryUnit = normalizeUnit((column.activity.unitOfMeasurementProduction || "Torres").toString().trim());
            const secondaryUnit = hasSecondaryUnit(column.activity) ? normalizeUnit(column.activity.unitOfMeasurement) : "";

            const isPrimaryMatch = primaryUnit === selectedUnitPerformanceHM;
            const isSecondaryMatch = secondaryUnit === selectedUnitPerformanceHM;

            if (!isPrimaryMatch && !isSecondaryMatch) return;

            const subActivity = column.activity.subActivity ? ` (${column.activity.subActivity})` : "";
            const activityName = `${column.activity.name}${subActivity}`;
            activityLabels.push(activityName || "");

            const activityData = Object.values(memoizedGroupedData)
                .flatMap(fronts => Object.values(fronts).flat())
                .filter(a => isMatchingActivity(a, column));

            let totalAdvance = 0;
            let totalHm = 0;
            let poValue = 0;

            if (isPrimaryMatch) {
                totalAdvance = activityData.reduce((sum, act) => sum + parseVal(act.totalAdvance), 0);
                totalHm = activityData.reduce((sum, act) => sum + parseVal(act.totalHm), 0);
                poValue = column.performaceHmPo ?? 0;
            } else if (isSecondaryMatch) {
                const secData = activityData.filter(a => normalizeUnit(a.unitOfMeasurement) === selectedUnitPerformanceHM);
                totalAdvance = secData.reduce((sum, act) => sum + parseVal(act.totalAdvance), 0);
                totalHm = secData.reduce((sum, act) => sum + parseVal(act.totalHm), 0);
                poValue = column.performanceUnitHmPo ?? 0;
            }

            const realValue = totalAdvance !== 0 ? totalHm / totalAdvance : 0;
            realValues.push(realValue);
            poValues.push(poValue);
        });

        return {
            labels: activityLabels,
            datasets: [
                { type: "bar", label: `Rendimiento (HM/${selectedUnitPerformanceHM}) - Real`, data: realValues, backgroundColor: "#0057B7", borderColor: "#004F86", borderWidth: 1, barThickness: 40 },
                { type: "line", label: `Rendimiento (HM/${selectedUnitPerformanceHM}) - PO`, data: poValues, borderColor: "#00FF00", backgroundColor: "#00FF00", borderWidth: 2, pointRadius: 3, fill: false }
            ]
        };
    }, [memoizedGroupedData, activitiesProject, selectedUnitPerformanceHM]);

    const chartOptionsUnitHm: ChartOptions<"bar" | "line"> = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: "top", }, }, scales: { x: { title: { display: true, text: "Actividades", }, }, y: { title: { display: true, text: `HM/${normalizeUnit(selectedUnitPerformanceHM)}`, }, beginAtZero: true, }, }, };

    const getTotalsGrouped = () => {
        const allActivities = Object.values(memoizedGroupedData).flatMap(fronts => Object.values(fronts).flat());
        const sumVal = (arr: any[], key: string) => arr.reduce((sum, act) => sum + (Number(act[key]) || 0), 0);

        const safeDiv = (num: number, den: number) => {
            const res = num / den;
            return (isNaN(res) || !isFinite(res)) ? 0 : res;
        };

        const getEquivalentDays = () => {
            if (!startDate || !endDate) return 1;
            const start = new Date(`${startDate}T00:00:00`);
            const end = new Date(`${endDate}T00:00:00`);
            let totalHours = 0;
            let current = new Date(start);
            while (current <= end) {
                const dayOfWeek = current.getDay();
                if (dayOfWeek >= 1 && dayOfWeek <= 3) totalHours += 8;
                else if (dayOfWeek === 4 || dayOfWeek === 5) totalHours += 7;
                else if (dayOfWeek === 6) totalHours += 4;
                current.setDate(current.getDate() + 1);
            }
            const eqDays = totalHours / 8;
            return eqDays > 0 ? eqDays : 1;
        };
        const diffDays = getEquivalentDays();

        const getMasterPO = (filterFn: (act: ProjectActivity) => boolean) => {
            const match = activitiesProject.find(filterFn);
            return {
                hh: Number(match?.performacePo || 0),
                hm: Number(match?.performaceHmPo || 0),
                hhUnit: Number(match?.performanceUnitPo || 0),
                hmUnit: Number(match?.performanceUnitHmPo || 0),
                prodGoal: Number(match?.productionGoal || 0),
                prodUnitGoal: Number(match?.performaceUnitGoal || 0),
                // Oferta: solo existe para la unidad de producción (no hay equivalente por unidad de avance).
                hhOffer: Number(match?.offerPerformacePo || 0),
                hmOffer: Number(match?.offerPerformaceHmPo || 0)
            };
        };

        const dataNiv = allActivities.filter(a => matchesAct(a, "nivelacion"));
        const dataHorm = allActivities.filter(a => matchesAct(a, "hormigonado"));

        const sumHhNiv = sumVal(dataNiv, "totalHh");
        const sumHmNiv = sumVal(dataNiv, "totalHm");
        const sumHhHorm = sumVal(dataHorm, "totalHh");
        const sumHmHorm = sumVal(dataHorm, "totalHm");

        const sumProdHorm = dataHorm.reduce((sum, act) => sum + parseVal(act.totalProduction), 0);

        const poNiv = getMasterPO(a => matchesAct(a, "nivelacion"));
        const poHorm = getMasterPO(a => matchesAct(a, "hormigonado"));

        // Metas P0 de fase desde 'Información Obra' (campos del proyecto), igual que el Excel.
        // Si el proyecto aún no tiene cargada la meta (0), se usa la suma de metas por actividad
        // como respaldo para no dejar la columna vacía.
        const num = (v: any) => Number(v || 0);
        const pick = (projectVal: number, fallback: number) => (projectVal > 0 ? projectVal : fallback);
        const civilWorksHh = num(projectData?.civilWorksHh);
        const civilWorksHm = num(projectData?.civilWorksHm);
        const assemblyHh = num(projectData?.assemblyHh);
        const assemblyHm = num(projectData?.assemblyHm);
        const conductorLayingHh = num(projectData?.conductorLayingHh);
        const conductorLayingHm = num(projectData?.conductorLayingHm);
        const opgwLayingHh = num(projectData?.opgwLayingHh);
        const opgwLayingHm = num(projectData?.opgwLayingHm);

        const rObrasHhReal = safeDiv((sumHhNiv + sumHhHorm), sumProdHorm);
        const rObrasHmReal = safeDiv((sumHmNiv + sumHmHorm), sumProdHorm);

        const prodObrasReal = sumProdHorm;
        const prodObrasP0 = poHorm.prodGoal * diffDays;

        const dataPreTorre = allActivities.filter(a => matchesAct(a, "pre-armado") && normText(a.unitOfMeasurementProduction).includes("torre"));
        const dataMontTorre = allActivities.filter(a => matchesAct(a, "montaje") && normText(a.unitOfMeasurementProduction).includes("torre"));

        const sumHhPreTorre = sumVal(dataPreTorre, "totalHh");
        const sumHmPreTorre = sumVal(dataPreTorre, "totalHm");
        const sumHhMontTorre = sumVal(dataMontTorre, "totalHh");
        const sumHmMontTorre = sumVal(dataMontTorre, "totalHm");

        const sumProdMontTorre = dataMontTorre.reduce((sum, act) => sum + parseVal(act.totalProduction), 0);

        const poPreTorre = getMasterPO(a => matchesAct(a, "pre-armado") && normText(a.activity.unitOfMeasurementProduction || "Torres").includes("torre"));
        const poMontTorre = getMasterPO(a => matchesAct(a, "montaje") && normText(a.activity.unitOfMeasurementProduction || "Torres").includes("torre"));

        const pmHhReal = safeDiv((sumHhPreTorre + sumHhMontTorre), sumProdMontTorre);
        const pmHmReal = safeDiv((sumHmPreTorre + sumHmMontTorre), sumProdMontTorre);

        const prodTorreReal = sumProdMontTorre;
        const prodTorreP0 = poMontTorre.prodGoal * diffDays;

        const dataPreTn = allActivities.filter(a => matchesAct(a, "pre-armado") && hasSecondaryUnit(a) && normText(a.unitOfMeasurement).includes("tn"));
        const dataMontTn = allActivities.filter(a => matchesAct(a, "montaje") && hasSecondaryUnit(a) && normText(a.unitOfMeasurement).includes("tn"));

        const sumHhPreTn = sumVal(dataPreTn, "totalHh");
        const sumHmPreTn = sumVal(dataPreTn, "totalHm");
        const sumHhMontTn = sumVal(dataMontTn, "totalHh");
        const sumHmMontTn = sumVal(dataMontTn, "totalHm");

        const sumAdvMontTn = dataMontTn.reduce((sum, act) => sum + parseVal(act.totalAdvance), 0);

        const poPreTn = getMasterPO(a => matchesAct(a, "pre-armado") && normText(a.activity.unitOfMeasurement || "") === "tn");
        const poMontTn = getMasterPO(a => matchesAct(a, "montaje") && normText(a.activity.unitOfMeasurement || "") === "tn");

        const rEstructTnHhReal = safeDiv((sumHhPreTn + sumHhMontTn), sumAdvMontTn);
        const rEstructTnHmReal = safeDiv((sumHmPreTn + sumHmMontTn), sumAdvMontTn);

        const prodTnReal = sumAdvMontTn;
        const prodTnP0 = poMontTn.prodUnitGoal * diffDays;

        // Tendido se separa en Conductor y OPGW (igual que el Excel: filas 78 y 79).
        // OPGW = actividades cuyo nombre contiene "OPGW"; el resto son de Conductor.
        let conductorHhReal = 0;
        let conductorHmReal = 0;
        let opgwHhReal = 0;
        let opgwHmReal = 0;
        // Respaldo de metas P0 (suma por actividad) por si el proyecto no tiene metas de fase cargadas.
        let conductorHhP0Fb = 0;
        let conductorHmP0Fb = 0;
        let opgwHhP0Fb = 0;
        let opgwHmP0Fb = 0;
        // Oferta de fase: suma de la oferta cargada por actividad. No hay meta de oferta a nivel
        // de proyecto (a diferencia de P0), así que esta suma es la única fuente.
        let conductorHhOffer = 0;
        let conductorHmOffer = 0;
        let opgwHhOffer = 0;
        let opgwHmOffer = 0;

        const factor = (statisticsData && statisticsData.totalTowers > 0) ? safeDiv(statisticsData.totalLength, statisticsData.totalTowers) : 1;
        const safeFactor = factor === 0 ? 1 : factor;

        const productiveActivities = activitiesProject.filter(col =>
            col.activity.section === "Tendido" &&
            col.activity.name !== "Empalmes OPGW" &&
            col.activity.name !== "Revisión de tendido"
        );

        productiveActivities.forEach(col => {
            const dailyReports = allActivities.filter(a => isMatchingActivity(a, col));

            const sumHh = sumVal(dailyReports, "totalHh");
            const sumHm = sumVal(dailyReports, "totalHm");

            const uomProd = normText(col.activity.unitOfMeasurementProduction || "");

            let hhContrib = 0;
            let hmContrib = 0;

            if (uomProd === "km") {
                const sumAdv = dailyReports.reduce((sum, act) => sum + parseVal(act.totalAdvance), 0);
                hhContrib = sumAdv > 0 ? safeDiv(sumHh, sumAdv) : 0;
                hmContrib = sumAdv > 0 ? safeDiv(sumHm, sumAdv) : 0;

            } else if (uomProd === "vanos") {
                const sumAdv = dailyReports.reduce((sum, act) => sum + parseVal(act.totalAdvance), 0);
                hhContrib = sumAdv > 0 ? safeDiv(safeDiv(sumHh, sumAdv), safeFactor) : 0;
                hmContrib = sumAdv > 0 ? safeDiv(safeDiv(sumHm, sumAdv), safeFactor) : 0;

            } else if (uomProd === "torres" || uomProd === "torre") {
                const sumProd = dailyReports.reduce((sum, act) => sum + parseVal(act.totalProduction), 0);
                hhContrib = sumProd > 0 ? safeDiv(safeDiv(sumHh, sumProd), safeFactor) : 0;
                hmContrib = sumProd > 0 ? safeDiv(safeDiv(sumHm, sumProd), safeFactor) : 0;
            }

            const poHh = Number(col.performacePo || 0);
            const poHm = Number(col.performaceHmPo || 0);
            const offerHh = Number(col.offerPerformacePo || 0);
            const offerHm = Number(col.offerPerformaceHmPo || 0);

            if (normText(col.activity.name).includes("opgw")) {
                opgwHhReal += hhContrib;
                opgwHmReal += hmContrib;
                opgwHhP0Fb += poHh;
                opgwHmP0Fb += poHm;
                opgwHhOffer += offerHh;
                opgwHmOffer += offerHm;
            } else {
                conductorHhReal += hhContrib;
                conductorHmReal += hmContrib;
                conductorHhP0Fb += poHh;
                conductorHmP0Fb += poHm;
                conductorHhOffer += offerHh;
                conductorHmOffer += offerHm;
            }
        });

        return {
            obras: {
                prodReal: prodObrasReal,
                prodP0: prodObrasP0,
                hhReal: rObrasHhReal,
                hhP0: pick(civilWorksHh, poNiv.hh + poHorm.hh),
                hhOffer: poNiv.hhOffer + poHorm.hhOffer,
                hmReal: rObrasHmReal,
                hmP0: pick(civilWorksHm, poNiv.hm + poHorm.hm),
                hmOffer: poNiv.hmOffer + poHorm.hmOffer
            },
            pmTorre: {
                prodReal: prodTorreReal,
                prodP0: prodTorreP0,
                hhReal: pmHhReal,
                hhP0: pick(assemblyHh, poPreTorre.hh + poMontTorre.hh),
                hhOffer: poPreTorre.hhOffer + poMontTorre.hhOffer,
                hmReal: pmHmReal,
                hmP0: pick(assemblyHm, poPreTorre.hm + poMontTorre.hm),
                hmOffer: poPreTorre.hmOffer + poMontTorre.hmOffer
            },
            pmTn: {
                prodReal: prodTnReal,
                prodP0: prodTnP0,
                hhReal: rEstructTnHhReal,
                hhP0: poPreTn.hhUnit + poMontTn.hhUnit,
                // La fila en Tn agrega la unidad de avance, que no captura oferta: siempre "-".
                hhOffer: 0,
                hmReal: rEstructTnHmReal,
                hmP0: poPreTn.hmUnit + poMontTn.hmUnit,
                hmOffer: 0
            },
            tendidoConductor: {
                hhReal: conductorHhReal,
                hhP0: pick(conductorLayingHh, conductorHhP0Fb),
                hhOffer: conductorHhOffer,
                hmReal: conductorHmReal,
                hmP0: pick(conductorLayingHm, conductorHmP0Fb),
                hmOffer: conductorHmOffer
            },
            tendidoOpgw: {
                hhReal: opgwHhReal,
                hhP0: pick(opgwLayingHh, opgwHhP0Fb),
                hhOffer: opgwHhOffer,
                hmReal: opgwHmReal,
                hmP0: pick(opgwLayingHm, opgwHmP0Fb),
                hmOffer: opgwHmOffer
            }
        };
    };

    return (
        <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} mb={1}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={showUnproductive}
                            onChange={(e) => setShowUnproductive(e.target.checked)}
                            color="primary"
                        />
                    }
                    label="Activar improductividades (Contar valores 0)"
                />
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Filtrar por Unidad</InputLabel>
                    <Select
                        value={selectedTableUnit}
                        label="Filtrar por Unidad"
                        onChange={(e) => setSelectedTableUnit(e.target.value)}
                    >
                        <MenuItem value="Todas"><em>Todas</em></MenuItem>
                        {availableTableUnits.map(unit => (
                            <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            <TableContainer component={Paper} sx={{ maxHeight: "800px", overflowY: "auto", mt: 2, mb: 4 }}>
                <Table stickyHeader sx={{ borderCollapse: "collapse", width: "100%" }} size="small" padding="none">
                    <TableHead>
                        <TableRow sx={{ height: "32px" }}>
                            <TableCell rowSpan={2} sx={{ fontWeight: "bold", backgroundColor: "#4472C4", color: "white", border: "1px solid #ccc", position: "sticky", left: 0, zIndex: 4, textAlign: "center" }}>Actividad</TableCell>
                            <TableCell rowSpan={2} sx={{ fontWeight: "bold", backgroundColor: "#4472C4", color: "white", border: "1px solid #ccc", textAlign: "center", zIndex: 3 }}>Unidad</TableCell>
                            <TableCell colSpan={4} sx={{ fontWeight: "bold", backgroundColor: "#4472C4", color: "white", border: "1px solid #ccc", textAlign: "center", zIndex: 3 }}>Producción (/día)</TableCell>
                            <TableCell colSpan={4} sx={{ fontWeight: "bold", backgroundColor: "#4472C4", color: "white", border: "1px solid #ccc", textAlign: "center", zIndex: 3 }}>Rendimiento HH</TableCell>
                            <TableCell colSpan={4} sx={{ fontWeight: "bold", backgroundColor: "#4472C4", color: "white", border: "1px solid #ccc", textAlign: "center", zIndex: 3 }}>Rendimiento HM</TableCell>
                        </TableRow>
                        <TableRow sx={{ height: "20px" }}>
                            {SUB_HEADERS.map((label, idx) => (
                                <TableCell key={idx} sx={{ fontWeight: "bold", backgroundColor: SUB_HEADER_COLORS[label], color: label === "Pendiente" ? "#94a3b8" : "inherit", border: "1px solid #ccc", textAlign: "center", zIndex: 3, fontSize: "11px", top: 32, borderRight: (idx + 1) % 4 === 0 ? "1px solid black" : "1px solid #ccc" }}>
                                    {label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {buildYieldDisplayRows().map((row) => {
                            const getColor = (realVal: string | number, p0Val: string | number, isProduction: boolean) => {
                                if (realVal === "-" || p0Val === "-" || realVal === "NaN" || p0Val === "NaN") return "inherit";
                                const realNum = parseFloat(realVal.toString());
                                const p0Num = parseFloat(p0Val.toString());
                                if (isProduction) return realNum >= p0Num ? "#C6E0B4" : "#F4CCCC";
                                return realNum <= p0Num ? "#C6E0B4" : "#F4CCCC";
                            };

                            return (
                                <TableRow
                                    key={row.key}
                                    hover
                                    sx={{
                                        height: "28px",
                                        "&:hover td": {
                                            backgroundColor: "#e3f2fd !important",
                                        }
                                    }}
                                >
                                    <TableCell sx={{ border: "1px solid #ccc", fontSize: "11px", padding: "4px 8px", position: "sticky", left: 0, backgroundColor: "white", zIndex: 1, fontWeight: row.isSecondary ? "normal" : "bold", pl: row.isSecondary ? 4 : 1 }}>
                                        {row.isSecondary ? "↳ " : ""}{row.name} {row.subActivity ? `(${row.subActivity})` : ""}
                                    </TableCell>
                                    <TableCell sx={{ border: "1px solid #ccc", fontSize: "11px", padding: "4px", textAlign: "center", backgroundColor: "white", color: row.isSecondary ? "gray" : "inherit" }}>
                                        {normalizeUnit(row.unitRaw)}
                                    </TableCell>
                                    {/* --- PRODUCCIÓN (/día): Oferta | P0 | Real | Pendiente --- */}
                                    <TableCell sx={{ ...offerCellSx, borderLeft: "1px solid black" }}>{row.prodOffer}</TableCell>
                                    <TableCell sx={{ border: "1px solid #ccc", fontSize: "11px", textAlign: "center", backgroundColor: "#FFEE99" }}>{row.prodP0}</TableCell>
                                    <TableCell sx={{ border: "1px solid #ccc", fontSize: "11px", textAlign: "center", backgroundColor: getColor(row.prodReal, row.prodP0, true) }}>{row.prodReal}</TableCell>
                                    <TableCell sx={pendingCellSx}>{row.prodPending}</TableCell>

                                    {/* --- RENDIMIENTO HH --- */}
                                    <TableCell sx={offerCellSx}>{row.hhOffer}</TableCell>
                                    <TableCell sx={{ border: "1px solid #ccc", fontSize: "11px", textAlign: "center", backgroundColor: "#FFEE99" }}>{row.hhP0}</TableCell>
                                    <TableCell sx={{ border: "1px solid #ccc", fontSize: "11px", textAlign: "center", backgroundColor: getColor(row.hhReal, row.hhP0, false) }}>{row.hhReal}</TableCell>
                                    <TableCell sx={pendingCellSx}>{row.hhPending}</TableCell>

                                    {/* --- RENDIMIENTO HM --- */}
                                    <TableCell sx={offerCellSx}>{row.hmOffer}</TableCell>
                                    <TableCell sx={{ border: "1px solid #ccc", fontSize: "11px", textAlign: "center", backgroundColor: "#FFEE99" }}>{row.hmP0}</TableCell>
                                    <TableCell sx={{ border: "1px solid #ccc", fontSize: "11px", textAlign: "center", backgroundColor: getColor(row.hmReal, row.hmP0, false) }}>{row.hmReal}</TableCell>
                                    <TableCell sx={pendingCellSx}>{row.hmPending}</TableCell>
                                </TableRow>
                            );
                        })}

                        {(() => {
                            const totals = getTotalsGrouped();

                            const renderColorRend = (real: number, p0: number) => {
                                if (real === 0 || p0 === 0) return "inherit";
                                return real <= p0 ? "#C6E0B4" : "#F4CCCC";
                            };

                            // Las filas de fase nunca informan producción por día: las 4 celdas
                            // del primer grupo van en "-", igual que antes de agregar Oferta.
                            const emptyProdCellSx = { border: "1px solid #ccc", backgroundColor: "#e9ecef", textAlign: "center" as const, color: "#ccc" };
                            const fmtTotal = (v: number) => (v > 0 ? v.toFixed(1) : "-");

                            type PhaseTotals = { hhReal: number; hhP0: number; hhOffer: number; hmReal: number; hmP0: number; hmOffer: number };

                            const PhaseTotalRow = ({ label, unit, t }: { label: string; unit: string; t: PhaseTotals }) => (
                                <TableRow hover sx={{ height: "28px" }}>
                                    <TableCell sx={{ border: "1px solid #ccc", fontSize: "11px", padding: "4px 8px", position: "sticky", left: 0, backgroundColor: "#f0f7fc", zIndex: 1, fontWeight: "bold" }}>{label}</TableCell>
                                    <TableCell sx={{ border: "1px solid #ccc", fontSize: "11px", padding: "4px", textAlign: "center", backgroundColor: "#f0f7fc", fontWeight: "bold" }}>{unit}</TableCell>

                                    {/* Producción (/día): Oferta | P0 | Real | Pendiente — sin dato a nivel de fase. */}
                                    <TableCell sx={{ ...emptyProdCellSx, borderLeft: "1px solid black" }}>-</TableCell>
                                    <TableCell sx={emptyProdCellSx}>-</TableCell>
                                    <TableCell sx={emptyProdCellSx}>-</TableCell>
                                    <TableCell sx={{ ...emptyProdCellSx, borderRight: "1px solid black" }}>-</TableCell>

                                    {/* Rendimiento HH */}
                                    <TableCell sx={{ ...offerCellSx, fontWeight: "bold" }}>{fmtTotal(t.hhOffer)}</TableCell>
                                    <TableCell sx={{ border: "1px solid #ccc", fontSize: "11px", textAlign: "center", backgroundColor: "#FFEE99", fontWeight: "bold" }}>{fmtTotal(t.hhP0)}</TableCell>
                                    <TableCell sx={{ border: "1px solid #ccc", fontSize: "11px", textAlign: "center", backgroundColor: renderColorRend(t.hhReal, t.hhP0), fontWeight: "bold" }}>{fmtTotal(t.hhReal)}</TableCell>
                                    <TableCell sx={pendingCellSx}>{PENDING_PLACEHOLDER}</TableCell>

                                    {/* Rendimiento HM */}
                                    <TableCell sx={{ ...offerCellSx, fontWeight: "bold" }}>{fmtTotal(t.hmOffer)}</TableCell>
                                    <TableCell sx={{ border: "1px solid #ccc", fontSize: "11px", textAlign: "center", backgroundColor: "#FFEE99", fontWeight: "bold" }}>{fmtTotal(t.hmP0)}</TableCell>
                                    <TableCell sx={{ border: "1px solid #ccc", fontSize: "11px", textAlign: "center", backgroundColor: renderColorRend(t.hmReal, t.hmP0), fontWeight: "bold" }}>{fmtTotal(t.hmReal)}</TableCell>
                                    <TableCell sx={pendingCellSx}>{PENDING_PLACEHOLDER}</TableCell>
                                </TableRow>
                            );

                            return (
                                <>
                                    <TableRow sx={{ backgroundColor: "#2e5b7f" }}>
                                        <TableCell colSpan={2} sx={{ padding: "6px", fontWeight: "bold", color: "white", fontSize: "11px", textAlign: "center", borderRight: "1px solid white", zIndex: 2 }}>
                                            RESUMEN DE RENDIMIENTOS (FASE)
                                        </TableCell>
                                        <TableCell colSpan={4} sx={{ padding: "6px", fontWeight: "bold", color: "white", fontSize: "11px", textAlign: "center", borderRight: "1px solid white", backgroundColor: "#3a6a8f" }}>
                                            Producción del Periodo
                                        </TableCell>
                                        <TableCell colSpan={4} sx={{ padding: "6px", fontWeight: "bold", color: "white", fontSize: "11px", textAlign: "center", borderRight: "1px solid white" }}>
                                            Rendimiento HH / Unidad
                                        </TableCell>
                                        <TableCell colSpan={4} sx={{ padding: "6px", fontWeight: "bold", color: "white", fontSize: "11px", textAlign: "center" }}>
                                            Rendimiento HM / Unidad
                                        </TableCell>
                                    </TableRow>

                                    <PhaseTotalRow label="Obras Civiles (Niv + Hormigón)" unit="Torre" t={totals.obras} />
                                    <PhaseTotalRow label="Estructuras (Pre + Montaje)" unit="Torre" t={totals.pmTorre} />
                                    <PhaseTotalRow label="Estructuras (Pre + Montaje)" unit="Tn" t={totals.pmTn} />
                                    <PhaseTotalRow label="Tendido Conductor" unit="Km" t={totals.tendidoConductor} />
                                    <PhaseTotalRow label="Tendido OPGW" unit="Km" t={totals.tendidoOpgw} />
                                </>
                            )
                        })()}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box display="flex" justifyContent="flex-end">
                <Button
                    variant="contained"
                    color="primary"
                    onClick={exportToExcel}
                    disabled={userRole === profiles[3].name}
                >
                    Descargar a Excel
                </Button>
            </Box>

            <TableContainer component={Paper} sx={{ maxHeight: "800px", overflowY: "auto", mt: 4 }}>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                        <CircularProgress />
                    </Box>
                ) : (
                    <Table stickyHeader sx={{ borderCollapse: "separate", borderSpacing: 0, width: "100%" }} size="small">
                        <TableHead>
                            <TableRow sx={{ height: "110px" }}>

                                {/* FECHA */}
                                <TableCell rowSpan={2} sx={{
                                    fontWeight: "bold", border: "1px solid black", textAlign: "center",
                                    fontSize: "12px", padding: "4px", backgroundColor: "white",
                                    position: "sticky", top: 0, left: 0, zIndex: 10,
                                    width: "70px", minWidth: "70px", maxWidth: "70px", boxSizing: "border-box"
                                }}>
                                    Fecha
                                </TableCell>

                                {/* FRENTE */}
                                <TableCell rowSpan={2} sx={{
                                    fontWeight: "bold", border: "1px solid black", fontSize: "12px",
                                    padding: "4px", backgroundColor: "white", textAlign: "center",
                                    position: "sticky", top: 0, left: 70, zIndex: 10,
                                    width: "70px", minWidth: "70px", maxWidth: "70px", boxSizing: "border-box"
                                }}>
                                    Frente
                                </TableCell>

                                {activitiesProject.map((column, index) => (
                                    <TableCell key={index} colSpan={hasSecondaryUnit(column.activity) ? 4 : 3} sx={{
                                        fontWeight: "bold", border: "1px solid #ccc", textAlign: "center",
                                        fontSize: "12px", padding: "4px", position: "sticky",
                                        top: 0, zIndex: 5, backgroundColor: "#78a1bf", color: "#004F86",
                                        borderRight: "1px solid black", borderLeft: "1px solid black",
                                        height: "110px", boxSizing: "border-box"
                                    }}>
                                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                            <span>{column.activity.name}</span>
                                            {column.activity.subActivity && (
                                                <span style={{ fontSize: "0.9em", color: "#ffffff" }}>
                                                    {column.activity.subActivity}
                                                </span>
                                            )}
                                        </div>
                                    </TableCell>
                                ))}
                            </TableRow>

                            <TableRow sx={{ height: "40px" }}>
                                {activitiesProject.map((column, index) => (
                                    [
                                        `Producción (${normalizeUnit(column.activity.unitOfMeasurementProduction || "Torre")})`,
                                        hasSecondaryUnit(column.activity) ? `Producción (${normalizeUnit(column.activity.unitOfMeasurement || "")})` : null,
                                        "HH",
                                        "HM"
                                    ].filter(Boolean).map((subcol, subIndex) => (
                                        <TableCell key={`${index}-${subIndex}`} sx={{
                                            fontWeight: "bold", border: "1px solid black", borderBottom: "1px solid black",
                                            textAlign: "center", fontSize: "12px", padding: "4px", backgroundColor: "white",
                                            position: "sticky",
                                            top: 110,
                                            zIndex: 5, boxSizing: "border-box"
                                        }}>
                                            {subcol}
                                        </TableCell>
                                    ))
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {Object.entries(memoizedGroupedData).map(([date, fronts]) => {
                                const dateRowSpan = Object.keys(fronts).length;

                                return Object.entries(fronts).map(([front, activities], frontIndex) => {
                                    return (
                                        <TableRow
                                            key={`${date}-${front}`}
                                            hover
                                            sx={{
                                                "&:hover td": {
                                                    backgroundColor: "#e3f2fd !important", // Resalte azul claro
                                                }
                                            }}
                                        >
                                            {frontIndex === 0 && (
                                                <TableCell
                                                    sx={{
                                                        fontWeight: "bold", border: "1px solid black", textAlign: "center",
                                                        fontSize: "12px", padding: "4px", backgroundColor: "white",
                                                        position: "sticky", left: 0, zIndex: 4,
                                                        width: "70px", minWidth: "70px", maxWidth: "70px", boxSizing: "border-box"
                                                    }}
                                                    rowSpan={dateRowSpan}
                                                >
                                                    {date}
                                                </TableCell>
                                            )}

                                            <TableCell
                                                sx={{
                                                    fontWeight: "bold", border: "1px solid black", fontSize: "12px",
                                                    padding: "4px", backgroundColor: "white", borderRight: "1px solid black",
                                                    textAlign: "center", position: "sticky", left: 70, zIndex: 4,
                                                    width: "70px", minWidth: "70px", maxWidth: "70px", boxSizing: "border-box"
                                                }}
                                            >
                                                {front}
                                            </TableCell>

                                            {activitiesProject.map((column, colIndex) => {
                                                const matchingActivities = activities.filter(a => isMatchingActivity(a, column));

                                                let displayProduction = "-";
                                                let displayAdvance = "-";
                                                let displayHH = "-";
                                                let displayHM = "-";

                                                const fmt = (num: number) => {
                                                    if (isNaN(num)) return "-";
                                                    return Number(num.toFixed(2)).toString();
                                                };

                                                if (matchingActivities.length > 0) {
                                                    const totalProd = matchingActivities.reduce((sum, item) => sum + parseVal(item.totalProduction), 0);
                                                    const totalAdv = matchingActivities.reduce((sum, item) => sum + parseVal(item.totalAdvance), 0);

                                                    // Producción en km (u otra unidad física) muestra el avance real, no el % agregado.
                                                    const primaryValue = isProductionInTorres(column.activity) ? totalProd : totalAdv;
                                                    displayProduction = primaryValue !== 0 ? fmt(primaryValue) : "-";

                                                    if (hasSecondaryUnit(column.activity)) {
                                                        displayAdvance = totalAdv !== 0 ? fmt(totalAdv) : "-";
                                                    }

                                                    const totalHHVal = matchingActivities.reduce((sum, item) => sum + parseVal(item.totalHh), 0);
                                                    if (totalHHVal !== 0) displayHH = fmt(totalHHVal);

                                                    const totalHMVal = matchingActivities.reduce((sum, item) => sum + parseVal(item.totalHm), 0);
                                                    if (totalHMVal !== 0) displayHM = fmt(totalHMVal);
                                                }

                                                return (
                                                    <Fragment key={colIndex}>
                                                        <TableCell sx={{ border: "1px solid #ccc", borderLeft: "1px solid black", fontSize: "12px", padding: "4px", textAlign: "center" }}>
                                                            {displayProduction}
                                                        </TableCell>
                                                        {hasSecondaryUnit(column.activity) && (
                                                            <TableCell sx={{ border: "1px solid #ccc", fontSize: "12px", padding: "4px", textAlign: "center" }}>
                                                                {displayAdvance}
                                                            </TableCell>
                                                        )}
                                                        <TableCell sx={{ border: "1px solid #ccc", fontSize: "12px", padding: "4px", textAlign: "center" }}>
                                                            {displayHH}
                                                        </TableCell>
                                                        <TableCell sx={{ border: "1px solid #ccc", borderRight: "1px solid black", fontSize: "12px", padding: "4px", textAlign: "center" }}>
                                                            {displayHM}
                                                        </TableCell>
                                                    </Fragment>
                                                );
                                            })}
                                        </TableRow>
                                    );
                                });
                            })}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>
            <Box display="flex" justifyContent="flex-end">
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => exportToExcel2(activitiesProject, groupedData)}
                    sx={{ mt: 2 }}
                    disabled={userRole === profiles[3].name}
                >
                    Descargar a Excel
                </Button>
            </Box>
            <Box>
                <Typography variant="h5" sx={{ mb: 2, fontWeight: "bold" }}>
                    Producción
                </Typography>
                <Paper sx={{ mb: 4 }}>
                    <Tabs value={selectedTabProduction} onChange={handleChangeProduction} centered>
                        <Tab label="Producción (torres/día)" />
                        <Tab label="Producción (unidad/día)" />
                    </Tabs>

                    <TabPanel value={selectedTabProduction} index={0}>
                        <Box display="flex" justifyContent="center" alignItems="center" height="400px">
                            <Chart data={chartDataProduction} options={chartOptionsProduction} type="bar" />
                        </Box>
                    </TabPanel>

                    <TabPanel value={selectedTabProduction} index={1}>
                        <FormControl sx={{ minWidth: 200, mb: 2 }}>
                            <InputLabel>Unidad de Medida</InputLabel>
                            <Select value={selectedUnit} onChange={(e) => setSelectedUnit(e.target.value)}>
                                {availableUnits.map(unit => (
                                    <MenuItem key={unit} value={unit}>{normalizeUnit(unit)}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Box display="flex" justifyContent="center" alignItems="center" height="400px">
                            <Chart data={chartDataProductionByUnit} options={chartOptions} type="bar" />
                        </Box>
                    </TabPanel>
                </Paper>

                <Typography variant="h5" sx={{ mb: 2, fontWeight: "bold" }}>
                    Rendimiento
                </Typography>
                <Paper>
                    <Tabs value={selectedTabPerformance} onChange={handleChangePerformance} centered>
                        <Tab label="Rendimiento (HH/Torre)" />
                        <Tab label="Rendimiento (HH/Unidad)" />
                        <Tab label="Rendimiento (HM/Torre)" />
                        <Tab label="Rendimiento (HM/Unidad)" />
                    </Tabs>

                    <TabPanel value={selectedTabPerformance} index={0}>
                        <Box display="flex" justifyContent="center" alignItems="center" height="400px">
                            <Chart data={chartData} options={chartOptionsHhTorre} type="bar" />
                        </Box>
                    </TabPanel>
                    <TabPanel value={selectedTabPerformance} index={1}>
                        <FormControl sx={{ minWidth: 200, mb: 2 }}>
                            <InputLabel>Unidad de Medida (Rendimiento)</InputLabel>
                            <Select value={selectedUnitPerformance} onChange={handleUnitChangePerformance}>
                                {availableUnitsPerformance.length > 0 ? (
                                    availableUnitsPerformance.map((unit: any) => (
                                        <MenuItem key={unit} value={unit}>{normalizeUnit(unit)}</MenuItem>
                                    ))
                                ) : (
                                    <MenuItem disabled>No hay unidades disponibles</MenuItem>
                                )}
                            </Select>
                        </FormControl>
                        <Box display="flex" justifyContent="center" alignItems="center" height="400px">
                            <Chart data={chartDataPerformanceByUnit} options={chartOptionsUnitHh} type={"bar"} />
                        </Box>
                    </TabPanel>
                    <TabPanel value={selectedTabPerformance} index={2}>
                        <Box display="flex" justifyContent="center" alignItems="center" height="400px">
                            <Chart data={chartDataHM} options={chartOptionsHm} type="bar" />
                        </Box>
                    </TabPanel>
                    <TabPanel value={selectedTabPerformance} index={3}>
                        <FormControl sx={{ minWidth: 200, mb: 2 }}>
                            <InputLabel>Unidad de Medida (Rendimiento HM)</InputLabel>
                            <Select value={selectedUnitPerformanceHM} onChange={handleUnitChangePerformanceHM}>
                                {availableUnitsPerformanceHM.length > 0 ? (
                                    availableUnitsPerformanceHM.map(unit => (
                                        <MenuItem key={unit} value={unit}>{normalizeUnit(unit)}</MenuItem>
                                    ))
                                ) : (
                                    <MenuItem disabled>No hay unidades disponibles</MenuItem>
                                )}
                            </Select>
                        </FormControl>
                        <Box display="flex" justifyContent="center" alignItems="center" height="400px">
                            <Chart data={chartDataPerformanceByUnitHM} options={chartOptionsUnitHm} type={"bar"} />
                        </Box>
                    </TabPanel>

                </Paper>
            </Box>
        </>
    );
};

export default ConsolidatedPerformanceReport;